'use es6';

import Raven from 'Raven';
import { List } from 'immutable';
import get from 'transmute/get';
import I18n from 'I18n';
import formatName from 'I18n/utils/formatName';
import * as TimeRangeListUtils from 'MeetingsBase/utils/TimeRangeListUtils';
import TimeRange from 'MeetingsBase/models/TimeRange';
import { LINK_TYPES, MEETING_TYPES, LINK_TYPE_PARAMS } from 'MeetingsBase/constants/enums';
import { wrongHubSpotDomain, wrongHubSpotQADomain, hubSpotDomain, hubSpotQADomain } from 'MeetingsLib/constants/paths';
import { getUrlDomain, getHubSpotIdentityInfo } from 'MeetingsLib/utils/analytics';
import { hubletApi } from 'hub-http/middlewares/core';
import { getFullUrl } from 'hubspot-url-utils';
import Url from 'urlinator/Url';
export var hubletAwareApi = hubletApi('api', 'hubspot', window.hubspot.meetingsHubletOverride);

function urlWithCrossDomainTracking(url) {
  var identityInfo = getHubSpotIdentityInfo();
  var firstSeparator = url.indexOf('?') !== -1 ? '&' : '?';
  return Object.keys(identityInfo).reduce(function (accUrl, param, idx) {
    var separator = idx ? '&' : firstSeparator;
    return "" + accUrl + separator + param + "=" + identityInfo[param];
  }, url);
}

export function redirectToUrl(url) {
  var embedded = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  // Support cross domain tracking https://issues.hubspotcentral.com/browse/SMMC-1524
  var sourceDomain = getUrlDomain(window.location.href);
  var redirectDomain = getUrlDomain(url);
  var redirectUrl = sourceDomain === redirectDomain ? url : urlWithCrossDomainTracking(url);

  if (embedded) {
    window.top.location.href = redirectUrl;
  } else {
    window.location.assign(redirectUrl);
  }
}
export function redirectToDomain(domain) {
  window.location.host = domain;
}
export function maybeRedirectToHubSpotDomain(domain) {
  if (domain === wrongHubSpotDomain) {
    redirectToDomain(hubSpotDomain);
  } else if (domain === wrongHubSpotQADomain) {
    redirectToDomain(hubSpotQADomain);
  }
}
export var getDomain = function getDomain(email) {
  return email.replace(/.*@/, '');
};
export function getAdminDomain() {
  return getFullUrl('app', {
    hubletOverride: window.hubspot.meetingsHubletOverride
  });
}
export function getLongDisplayName(profile) {
  if (profile.firstName && profile.lastName) {
    return formatName({
      firstName: profile.firstName,
      lastName: profile.lastName
    });
  }

  return profile.fullName || profile.firstName || profile.name || profile.email;
}
export function getIsRoundRobin(linkType) {
  return linkType === LINK_TYPES.get(MEETING_TYPES.ROUND_ROBIN);
}
export function getLinkEditParam(linkType) {
  var isTeamLink = getIsRoundRobin(linkType) || linkType === LINK_TYPES.get(MEETING_TYPES.GROUP);
  return isTeamLink ? LINK_TYPE_PARAMS.get('team') : LINK_TYPE_PARAMS.get(MEETING_TYPES.PERSONAL);
}
export function getHeaderType(isRoundRobin) {
  return isRoundRobin ? 'roundRobinHeader' : 'header';
}
export function getHeaderOptions(isRoundRobin, companyName, profile) {
  return isRoundRobin ? {
    companyName: companyName || getDomain(profile.email)
  } : {
    fullName: I18n.SafeString(getLongDisplayName(profile))
  };
}
export function setDocumentTitle(headline, linkType, companyName, profile) {
  var isRoundRobin = getIsRoundRobin(linkType);
  var headerType = getHeaderType(isRoundRobin);
  var options = getHeaderOptions(isRoundRobin, companyName, profile);
  var profileHeader = I18n.text("prebook." + headerType + ".online", options);
  document.title = headline || profileHeader;
}

function getRandomIndex(length) {
  return Math.floor(Math.random() * length);
}

export function isWhitespace(value) {
  return typeof value === 'string' && value.trim().length <= 0;
}
export function isEmpty(value) {
  return !value || value.trim() === '';
}
export function formatCustomFormFields(formFields) {
  return formFields.reduce(function (acc, fieldAnswer, fieldName) {
    if (fieldAnswer === null) {
      // if unrequired field is left blank, don't send anything
      return acc;
    }

    var isDateField = fieldAnswer.year && fieldAnswer.month && fieldAnswer.date;
    var isListField = Array.isArray(fieldAnswer);

    if (isDateField) {
      fieldAnswer = I18n.moment.utc([fieldAnswer.year, fieldAnswer.month, fieldAnswer.date]).valueOf().toString();
    }

    if (isListField) {
      fieldAnswer = fieldAnswer.join(';');
    }

    return acc.push({
      name: fieldName,
      value: fieldAnswer
    });
  }, List());
}
export function getOnlineOfflineBookableUsers(bufferTime, meeting, allUsersBusyTimes) {
  var onlineOffline = allUsersBusyTimes.map(function (userBusyTimes) {
    return userBusyTimes.update('busyTimes', function (busyTimes) {
      return TimeRangeListUtils.addBufferTime(busyTimes, bufferTime);
    });
  }).filter(function (paddedUserBusyTimes) {
    return paddedUserBusyTimes.get('busyTimes').every(function (paddedBusyTime) {
      return !paddedBusyTime.hasIntersection(meeting);
    });
  }).groupBy(function (availableUsers) {
    return availableUsers.get('offline');
  });
  return {
    onlineBookableUsers: onlineOffline.get(false),
    offlineBookableUsers: onlineOffline.get(true)
  };
}
export function getRoundRobinUserIdToBook(event, allUsersBusyTimes, bufferTime) {
  var dateTime = event.dateTime,
      duration = event.duration;
  var meeting = TimeRange.from({
    start: dateTime,
    end: dateTime + duration
  });

  var _getOnlineOfflineBook = getOnlineOfflineBookableUsers(bufferTime, meeting, allUsersBusyTimes),
      onlineBookableUsers = _getOnlineOfflineBook.onlineBookableUsers,
      offlineBookableUsers = _getOnlineOfflineBook.offlineBookableUsers;

  if (onlineBookableUsers && onlineBookableUsers.size) {
    var chosenUserId = onlineBookableUsers.get(getRandomIndex(onlineBookableUsers.size)).get('id');
    var allUserIds = onlineBookableUsers.map(function (user) {
      return user.get('id');
    });
    return {
      chosenUserId: chosenUserId,
      allUserIds: allUserIds
    };
  } else if (offlineBookableUsers && offlineBookableUsers.size) {
    var _chosenUserId = offlineBookableUsers.get(getRandomIndex(offlineBookableUsers.size)).get('id');

    return {
      chosenUserId: _chosenUserId,
      allUserIds: null
    };
  }

  Raven.captureMessage('No users bookable on round robin link', {
    extra: {
      allUsersBusyTimes: allUsersBusyTimes,
      bufferTime: bufferTime,
      meeting: meeting
    }
  });
  return {
    chosenUserId: null,
    allUserIds: null
  };
}
export function shouldIncludeDeleted(params) {
  return get('includeDeleted', params) || 'false';
}
export function getMeetingSourceFromLocation() {
  var url = new Url(window.location);
  var meetingSource = url.paramValue('meetingSource') || '';
  return meetingSource;
}